import React from 'react';
import { Outlet } from 'react-router-dom';
import * as propz from 'propz';
import { styled, experimental_sx as sx } from '@mui/system';
import { School } from 'models/school';
import { Tournament } from 'models/tournament';
import { SchoolLeague } from 'models/league';
import { LAYOUT_TYPE } from 'consts/layout';
import { StorageHelper } from 'helpers/storage';
import Box from '@mui/material/Box';
import Drawer from 'components/Drawer';
import Footer from 'components/Footer';
import CookieConsent from 'components/CookieConsent';
import SchoolPublicLayout from 'components/SchoolPublicLayout';
import { getAccessibleBrandColor, mixBrandColor, resolveSchoolPublicSite } from 'helpers/publicSite';

interface Props {
   school: School;
   league?: SchoolLeague;
   challenge?: SchoolLeague;
   tournament?: Tournament;
   type?: string;
}

const AppWrapper = styled(Box)({
   display: 'flex',
   height: '100%'
});

const Content = styled(Box)(
   sx({
      marginTop: {
         xs: '56px',
         sm: '62px'
      },
      display: 'flex',
      flexDirection: 'column',
      flex: 'auto',
      minHeight: '99%'
   })
);

export default function Layout(props: Props) {
   const { school, league, challenge, tournament, type = LAYOUT_TYPE.SCHOOL } = props;

   if (type === LAYOUT_TYPE.SCHOOL || type === LAYOUT_TYPE.SCHOOL_UNION) {
      return <SchoolPublicLayout school={school} />;
   }

   const leagueName = propz.get(league, ['name'], '');
   const challengeName = propz.get(challenge, ['name'], '');
   const tournamentName = propz.get(tournament, ['name'], '');

   const isLeague = typeof league !== 'undefined';
   const isChallenge = typeof challenge !== 'undefined';
   const isTournament = typeof tournamentName !== 'undefined';

   let title;

   switch (true) {
      case isLeague:
         title = leagueName;
         break;

      case isChallenge:
         title = challengeName;
         break;

      case isTournament:
         title = tournamentName;
         break;
   
      default:
         break;
   }

   const isCookiesAccepted = StorageHelper.LocalStorage.get('isCookiesAccepted');
   const publicSiteSettings = resolveSchoolPublicSite(school);
   const accessibleBrand = getAccessibleBrandColor(publicSiteSettings.appearance.brandColor);
   const cssVariables = {
      '--sc-brand': accessibleBrand,
      '--sc-brand-soft': mixBrandColor(accessibleBrand, '#ffffff', 0.88),
      '--sc-brand-muted': mixBrandColor(accessibleBrand, '#ffffff', 0.68),
      '--sc-brand-deep': mixBrandColor(accessibleBrand, '#000000', 0.18),
   } as React.CSSProperties;

   const isTeamTournamentV2 = type === LAYOUT_TYPE.FOOTBALL_TOURNAMENT
      && tournament?.publicSite?.templateVersion === 'TEAM_TOURNAMENT_V2';
   const isIndividualTournamentV2 = type === LAYOUT_TYPE.TOURNAMENT
      && tournament?.publicSite?.templateVersion === 'INDIVIDUAL_TOURNAMENT_V2';

   if (isTeamTournamentV2 || isIndividualTournamentV2) {
      return (
         <div style={{...cssVariables, minHeight: '100vh'}}>
            <Outlet />
            {!isCookiesAccepted && <CookieConsent />}
         </div>
      );
   }

   return (
      <AppWrapper style={cssVariables}>
         <Drawer school={school} type={type} titleText={title} />

         <Content id='main-content' sx={{overflowY: 'auto'}}>
            <Box sx={{ flex: '1 0 auto' }}>
               <Outlet />
            </Box>

            <Box sx={{ flex: '0 0 auto' }}>
               <Footer />
            </Box>
         </Content>

         {!isCookiesAccepted && <CookieConsent />}
      </AppWrapper>
   )
}
